import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { whatsappWithText } from "@/lib/site";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export const FloatingWhatsApp = () => {
  const [teaser, setTeaser] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("wa-teaser-dismissed")) return;
    const t = setTimeout(() => setTeaser(true), 4000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setTeaser(false);
    sessionStorage.setItem("wa-teaser-dismissed", "1");
  };

  return (
    <>
      <AnimatePresence>
        {teaser && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-50 w-[300px] rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
            data-testid="whatsapp-teaser"
          >
            <div className="flex items-center gap-3 bg-[#075E54] px-4 py-3 text-white">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#25D366] shrink-0">
                <WhatsAppIcon className="h-5 w-5" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold leading-tight truncate">MBA Project &amp; Thesis Help</p>
                <p className="text-[11px] text-white/80 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-400" /> Online — replies in minutes
                </p>
              </div>
              <button
                onClick={dismiss}
                data-testid="whatsapp-teaser-close"
                aria-label="Dismiss chat preview"
                className="p-1 text-white/80 hover:text-white transition-colors duration-150"
              >
                <X className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>
            <div className="p-4 bg-[#ECE5DD]">
              <div className="rounded-xl rounded-tl-none bg-white px-3.5 py-2.5 text-sm text-slate-700 leading-relaxed shadow-sm">
                Hi there! Stuck with a project or thesis? Tell us your topic and deadline — we'll send you a free quote.
              </div>
            </div>
            <div className="p-3 bg-white">
              <a
                href={whatsappWithText("Hi, I'd like a free quote for academic writing help.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-teaser-start"
                onClick={dismiss}
                className="flex justify-center items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5"
              >
                <WhatsAppIcon className="h-4 w-4" /> Start Chat
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <a
        href={whatsappWithText("Hi, I'd like a free quote for academic writing help.")}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-whatsapp-button"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-6 right-6 z-50 group"
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-25" />
        <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg shadow-[#25D366]/30 transition-transform duration-300 group-hover:scale-110">
          <WhatsAppIcon className="h-7 w-7" />
        </span>
      </a>
    </>
  );
};

export { WhatsAppIcon };
